// Tryb — field validation.
//
// Pure functions over (fieldDef, vals). Shared by the browser (live inline
// errors as you type) and by the API (the authoritative check before anything
// is persisted or published), so a client can never talk the server into
// storing an answer the studio would have rejected.

import { BY_ID, ENUMS } from "./fields.js";

export function empty(v) {
  return (
    v === undefined ||
    v === null ||
    v === "" ||
    (Array.isArray(v) && !v.length) ||
    (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0)
  );
}

export function isVisible(def, vals) {
  return !def.showIf || def.showIf(vals);
}

export function isRequired(def, vals) {
  return def.req === "req" || (def.reqIf ? def.reqIf(vals) : false);
}

export function validate(def, vals) {
  const v = vals[def.id];
  const req = isRequired(def, vals);

  if (def.type === "ro" || def.type === "dates") return null;

  if (def.type === "bool" || def.type === "boolfile") {
    if (def.mustBeTrue && req && v !== true) return "We need a yes here";
    return null;
  }

  if (def.type === "range") {
    if (!v || empty(v.lo) || empty(v.hi)) return req ? "Both ends, please" : null;
    if (+v.lo < 18) return "Hosts and members are 18+";
    if (+v.hi - +v.lo > 15) return "Keep it within fifteen years — mixed ages churn";
    if (+v.hi < +v.lo) return "The top should be higher than the bottom";
    return null;
  }

  if (def.type === "timerange" || def.type === "namephone") {
    if (!v || (empty(v.lo) && empty(v.hi))) return req ? "Needed" : null;
    if (empty(v.lo) || empty(v.hi)) return "Both parts, please";
    return null;
  }

  if (empty(v)) return req ? "Needed" : null;

  if (def.type === "cover" || ["file", "image", "images"].includes(def.type)) return null;

  const s = String(v);

  if (def.re && !def.re.test(s)) {
    return def.id === "A1.04"
      ? "PAN looks off — five letters, four digits, one letter"
      : def.id === "A5.08"
        ? "IFSC is eleven characters, like HDFC0001234"
        : def.id === "A5.07"
          ? "Digits only, nine to eighteen of them"
          : def.id === "A5.01"
            ? "UPI IDs look like name@bank"
            : "That doesn’t look right";
  }

  if (["text", "textarea", "address", "url", "email", "phone"].includes(def.type)) {
    if (def.min && s.length < def.min) return `A bit more — at least ${def.min} characters (${s.length} so far)`;
    if (def.max && s.length > def.max) return `Trim it to ${def.max} characters (${s.length} now)`;
    if (def.type === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s)) return "That email doesn’t look complete";
    if (def.type === "phone" && s.replace(/\D/g, "").length < 10) return "Ten digits, please";
    if (def.type === "url" && !/^https?:\/\//.test(s)) return "Start with https://";
    if (def.type === "url" && /\d{10}/.test(s)) return "No phone numbers in links, please";
    if (def.id === "B1.02" && s.length > 8 && s === s.toUpperCase()) return "Easy on the caps";
    if (def.matchField && vals[def.matchField] && s.trim().toLowerCase() !== String(vals[def.matchField]).trim().toLowerCase())
      return "Has to match the name on your ID";
  }

  if (def.type === "int" || def.type === "money") {
    const n = +v;
    if (isNaN(n)) return "Numbers only";
    if (def.min !== undefined && n < def.min) return `Can’t go below ${def.min}`;
    if (def.max !== undefined && n > def.max) return `Tops out at ${def.max}`;
    if (def.ltField && vals[def.ltField] !== undefined && n >= +vals[def.ltField]) return "Has to be lower than the maximum";
    if (def.gteField && vals[def.gteField] !== undefined && n < +vals[def.gteField]) return "The venue needs to fit the whole group";
    if (def.pctOf && vals[def.pctOf.field] && n > +vals[def.pctOf.field] * def.pctOf.pct)
      return `Keep the trial under ₹${Math.round(+vals[def.pctOf.field] * def.pctOf.pct)} — a quarter of the season`;
  }

  if (def.type === "date") {
    const d = new Date(s);
    if (isNaN(d)) return "Pick a date";
    if (def.minAge) {
      const age = (Date.now() - d) / 31557600000;
      if (age < def.minAge) return "Hosts need to be 18 or over";
    }
    if (def.minDaysAhead && (d - Date.now()) / 86400000 < def.minDaysAhead)
      return `Give people at least ${def.minDaysAhead} days’ notice`;
  }

  if (def.type === "multi" && def.max && v.length > def.max) return `Up to ${def.max}`;

  return null;
}

/** Required-answer tally for a group of field ids — "3/7 done" in the UI. */
export function countReq(ids, vals) {
  let done = 0;
  let total = 0;
  ids.forEach((id) => {
    const d = BY_ID[id];
    if (!d || !isVisible(d, vals) || !isRequired(d, vals)) return;
    total++;
    if (!validate(d, vals)) done++;
  });
  return { done, total };
}

/** True when any visible field in the group currently fails validation. */
export function hasErrors(ids, vals) {
  return ids.some((id) => {
    const d = BY_ID[id];
    return d && isVisible(d, vals) && validate(d, vals);
  });
}

/** Every validation error across a group, keyed by field id. */
export function errorsFor(ids, vals) {
  const out = {};
  ids.forEach((id) => {
    const d = BY_ID[id];
    if (!d || !isVisible(d, vals)) return;
    const e = validate(d, vals);
    if (e) out[id] = e;
  });
  return out;
}

/**
 * Lay out the season's session dates from the start date, weekday and cadence,
 * marking the ones that fall on a declared blackout day.
 */
export function sessionDates(vals) {
  const start = vals["D1.01"] ? new Date(vals["D1.01"]) : null;
  if (!start || isNaN(start)) return [];

  const wd = ENUMS.WEEKDAY.indexOf(vals["B2.04"]);
  const js = wd < 0 ? start.getDay() : (wd + 1) % 7;
  const count = Math.min(+vals["B2.02"] || 6, 12);
  const step = vals["B2.01"] === "Fortnightly series" ? 14 : 7;
  const black = (vals["D1.04"] || []).map((b) => b.split(" · ")[0]);

  const d = new Date(start);
  while (d.getDay() !== js) d.setDate(d.getDate() + 1);

  const out = [];
  let guard = 0;
  while (out.filter((x) => !x.skipped).length < count && guard++ < 40) {
    const key = `${d.getDate()} ${d.toLocaleDateString("en-IN", { month: "short" })}`;
    out.push({ date: new Date(d), skipped: black.includes(key) });
    d.setDate(d.getDate() + step);
  }
  return out;
}

export function fmtDate(d) {
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}

export function fmtSize(n) {
  return n > 1048576 ? (n / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(n / 1024)) + " KB";
}
